<script>
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../../stores/authStore.svelte.js';
  import PageHeader from '../../components/PageHeader.svelte';
  import DataTable from '../../components/DataTable.svelte';
  import Pagination from '../../components/Pagination.svelte';
  import ConfirmModal from '../../components/ConfirmModal.svelte';

  let loading = $state(true);
  let error = $state(null);
  let items = $state([]);
  let pagination = $state(null);
  let page = $state(1);

  let invitations = $state([]);
  let showInvite = $state(false);
  let inviteEmail = $state('');
  let inviteRole = $state('property_owner');
  let inviteTenantId = $state(null);
  let inviteProperties = $state([]);
  let availableProperties = $state([]);
  let submitting = $state(false);

  let confirmTarget = $state(null);

  // Access editor. Opening this is what a row click does now — removing access
  // is one button inside it, rather than the row click itself, which used to
  // fire a destructive confirm on a stray click.
  let editTarget = $state(null);
  let editProperties = $state([]);
  let editAvailable = $state([]);
  let editLoading = $state(false);
  let savingGrants = $state(false);

  async function openEditor(row) {
    editTarget = row;
    editProperties = [...(row.property_ids ?? [])];
    editAvailable = [];
    if (row.role !== 'property_owner' || !row.tenant_id) return;
    editLoading = true;
    try {
      const result = await api.listProperties({ tenant_id: row.tenant_id, limit: 100 });
      editAvailable = result.items;
    } catch (e) {
      toast.error(e.message);
    } finally {
      editLoading = false;
    }
  }

  function toggleEditProperty(id) {
    editProperties = editProperties.includes(id)
      ? editProperties.filter((p) => p !== id)
      : [...editProperties, id];
  }

  async function saveGrants() {
    const target = editTarget;
    savingGrants = true;
    try {
      // The endpoint replaces grants wholesale, which is why the editor sends
      // the full selection rather than a diff.
      await api.setPropertyGrants(target.user_id, editProperties.map(Number),
                                  { membership_tenant_id: target.tenant_id ?? undefined });
      toast.success(`Updated property access for ${target.email}`);
      editTarget = null;
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      savingGrants = false;
    }
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const [users, invites] = await Promise.all([
        api.listPortalUsers({ page, limit: 25 }),
        api.listInvitations({ status: 'pending' }),
      ]);
      items = users.items;
      pagination = users.pagination;
      invitations = invites;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { page; load(); });

  async function loadPropertiesFor(tenantId) {
    if (!tenantId) { availableProperties = []; return; }
    const result = await api.listProperties({ tenant_id: tenantId, limit: 100 });
    availableProperties = result.items;
  }

  $effect(() => { loadPropertiesFor(inviteTenantId); });

  async function submitInvite(event) {
    event.preventDefault();
    submitting = true;
    try {
      const result = await api.createInvitation({
        email: inviteEmail,
        tenant_id: Number(inviteTenantId),
        role: inviteRole,
        property_ids: inviteRole === 'property_owner' ? inviteProperties.map(Number) : [],
      });
      // The server reports whether the invitation email actually went out —
      // SMTP is optional, so never claim a send that may not have happened.
      if (result?.email_sent) {
        toast.success(`Invitation emailed to ${inviteEmail}`);
      } else {
        toast.info(
          `Invitation created for ${inviteEmail}, but no email was sent. `
          + 'Send them the portal link yourself.'
        );
      }
      showInvite = false;
      inviteEmail = '';
      inviteProperties = [];
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      submitting = false;
    }
  }

  async function revoke(id) {
    try {
      await api.revokeInvitation(id);
      toast.success('Invitation revoked');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function deactivate() {
    const target = confirmTarget;
    confirmTarget = null;
    editTarget = null;
    try {
      await api.deactivateUser(target.user_id, { membership_tenant_id: target.tenant_id ?? undefined });
      toast.success(`Access removed for ${target.email}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  }

  const roleLabels = {
    super_user: 'Super user',
    pm_staff: 'PM staff',
    property_owner: 'Property owner',
  };

  const columns = [
    { key: 'email', label: 'Email' },
    { key: 'full_name', label: 'Name', render: (r) => r.full_name ?? '—' },
    { key: 'role', label: 'Role', render: (r) => roleLabels[r.role] ?? r.role },
    { key: 'tenant_name', label: 'Tenant', render: (r) => r.tenant_name ?? 'All' },
    {
      key: 'property_count', label: 'Properties', align: 'right',
      render: (r) => (r.role === 'property_owner' ? String(r.property_count) : 'All'),
    },
    { key: 'is_active', label: 'Status', render: (r) => (r.is_active ? 'Active' : 'Disabled') },
  ];
</script>

<PageHeader title="People &amp; access" subtitle="Portal accounts, roles and property grants.">
  {#snippet children()}
    <button type="button" class="p-btn p-btn-primary" onclick={() => (showInvite = !showInvite)}>
      <i class="fas fa-user-plus" aria-hidden="true"></i> Invite
    </button>
  {/snippet}
</PageHeader>

{#if showInvite}
  <form class="p-card mb-6 space-y-4 p-4" onsubmit={submitInvite}>
    <div class="grid gap-4 sm:grid-cols-3">
      <div>
        <label for="inv-email" class="text-xs font-medium p-muted">Email</label>
        <input id="inv-email" class="p-input mt-1" type="email" bind:value={inviteEmail} required />
      </div>
      <div>
        <label for="inv-role" class="text-xs font-medium p-muted">Role</label>
        <select id="inv-role" class="p-input mt-1" bind:value={inviteRole}>
          <option value="property_owner">Property owner</option>
          <option value="pm_staff">PM staff</option>
        </select>
      </div>
      <div>
        <label for="inv-tenant" class="text-xs font-medium p-muted">Tenant</label>
        <select id="inv-tenant" class="p-input mt-1" bind:value={inviteTenantId} required>
          <option value={null}>Select…</option>
          {#each authStore.availableTenants.filter((t) => t.is_active !== false) as tenant}
            <option value={tenant.id}>{tenant.name}</option>
          {/each}
        </select>
      </div>
    </div>

    {#if inviteRole === 'property_owner'}
      <div>
        <label for="inv-props" class="text-xs font-medium p-muted">
          Properties ({inviteProperties.length} selected)
        </label>
        <select id="inv-props" class="p-input mt-1 h-40" multiple bind:value={inviteProperties}>
          {#each availableProperties as property}
            <option value={property.id}>{property.name}</option>
          {/each}
        </select>
        <p class="mt-1 text-xs p-faint">
          An owner with no properties selected can sign in but will see nothing.
        </p>
      </div>
    {/if}

    <p class="rounded-lg px-3 py-2 text-xs"
       style="background-color: var(--p-warn-bg, rgba(180,120,0,.12))">
      <i class="fas fa-circle-info" aria-hidden="true"></i>
      They will be emailed a link to create their account. Access is granted the moment they
      register with this exact address — so if they sign up with a different one, it will not
      apply. If email is not configured for this deployment you will be told, and can send them
      <code>/strao/register</code> yourself.
    </p>

    <div class="flex justify-end gap-2">
      <button type="button" class="p-btn p-btn-quiet" onclick={() => (showInvite = false)}>Cancel</button>
      <button type="submit" class="p-btn p-btn-primary" disabled={submitting || !inviteTenantId}>
        Create invitation
      </button>
    </div>
  </form>
{/if}

{#if invitations.length}
  <section class="mb-6">
    <h2 class="mb-2 text-sm font-semibold">Pending invitations</h2>
    <p class="mb-2 text-xs p-faint">
      Waiting for the invitee to register with this email address.
    </p>
    <div class="p-card divide-y" style="border-color: var(--p-border)">
      {#each invitations as invite (invite.id)}
        <div class="flex items-center justify-between gap-4 px-4 py-3 text-sm">
          <div>
            <p>{invite.invitee_email}</p>
            <p class="text-xs p-faint">
              {roleLabels[invite.role] ?? invite.role}
              · {invite.property_ids.length} propert{invite.property_ids.length === 1 ? 'y' : 'ies'}
              · expires {new Date(invite.expires_at).toLocaleDateString()}
            </p>
          </div>
          <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                  onclick={() => revoke(invite.id)}>Revoke</button>
        </div>
      {/each}
    </div>
  </section>
{/if}

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else}
  <h2 class="mb-2 text-sm font-semibold">Accounts</h2>
  <DataTable {columns} rows={items} {loading}
             onRowClick={(row) => row.is_active && row.user_id !== authStore.context?.user_id
                                  && openEditor(row)} />
  <Pagination {pagination} onPage={(p) => (page = p)} />
  <p class="mt-2 text-xs p-faint">Select an account to manage its property access.</p>
{/if}

{#if editTarget}
  <div class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
       role="presentation" onclick={() => (editTarget = null)}>
    <div class="p-card w-full max-w-lg p-5" role="dialog" aria-modal="true"
         aria-label="Manage access" onclick={(e) => e.stopPropagation()}>
      <h2 class="text-sm font-semibold">Manage access</h2>
      <p class="mt-1 text-xs p-faint">
        {editTarget.email} · {roleLabels[editTarget.role] ?? editTarget.role}
        · {editTarget.tenant_name ?? 'All tenants'}
      </p>

      {#if editTarget.role !== 'property_owner'}
        <p class="mt-4 text-sm p-muted">
          {roleLabels[editTarget.role] ?? editTarget.role} accounts see every property in their
          tenant, so there are no individual grants to edit.
        </p>
      {:else if editLoading}
        <p class="mt-4 text-sm p-muted">Loading properties…</p>
      {:else if !editAvailable.length}
        <p class="mt-4 text-sm p-muted">This tenant has no properties yet.</p>
      {:else}
        <p class="mt-4 text-xs font-medium p-muted">
          Properties ({editProperties.length} of {editAvailable.length} selected)
        </p>
        <div class="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-lg border p-2"
             style="border-color: var(--p-border)">
          {#each editAvailable as property (property.id)}
            <label class="flex items-center gap-2 rounded px-2 py-1 text-sm">
              <input type="checkbox" checked={editProperties.includes(property.id)}
                     onchange={() => toggleEditProperty(property.id)} />
              <span>{property.name}</span>
            </label>
          {/each}
        </div>
        <p class="mt-1 text-xs p-faint">
          Saving replaces this owner's grants with exactly what is checked. An owner with
          nothing checked can sign in but will see nothing.
        </p>
      {/if}

      <div class="mt-5 flex items-center justify-between gap-2">
        <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                style="color: var(--p-danger)"
                onclick={() => (confirmTarget = editTarget)}>
          Remove portal access
        </button>
        <div class="flex gap-2">
          <button type="button" class="p-btn p-btn-quiet" onclick={() => (editTarget = null)}>
            Cancel
          </button>
          {#if editTarget.role === 'property_owner'}
            <button type="button" class="p-btn p-btn-primary"
                    disabled={savingGrants || editLoading} onclick={saveGrants}>
              Save access
            </button>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}

<ConfirmModal open={!!confirmTarget}
              title="Remove portal access?"
              message={confirmTarget
                ? `${confirmTarget.email} will immediately lose access to the portal. Their Hostaway and guest data are unaffected.`
                : ''}
              confirmLabel="Remove access"
              onConfirm={deactivate}
              onCancel={() => (confirmTarget = null)} />
